import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient, Ticket } from '../lib/api';
import { DataTable } from './common/DataTable';
import toast from 'react-hot-toast';
import { 
    Search, 
    Filter, 
    LifeBuoy, 
    MessageSquare,
    Building,
    RefreshCw
} from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import TicketDetailModalComponent from './TicketDetailModalComponent';

const TicketsPage: React.FC = () => {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: 'all',
        priority: 'all',
        dateRange: 'all',
        search: '',
        temple: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
    const [temples, setTemples] = useState<any[]>([]);

    const fetchTemples = async () => {
        try {
            const res = await adminApiClient.getTemples({ limit: 1000 });
            if (res.success && res.data) {
                setTemples(res.data.temples || []);
            }
        } catch (error) {
            console.error('Error fetching temples:', error);
        }
    };

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (filters.status !== 'all') params.status = filters.status;
            if (filters.priority !== 'all') params.priority = filters.priority;
            if (filters.dateRange !== 'all') params.dateRange = filters.dateRange;
            if (filters.search) params.search = filters.search;
            if (filters.temple) params.temple = filters.temple;

            const res = await adminApiClient.getTickets(params);
            if (res.success && res.data) {
                setTickets(res.data.tickets || []);
            }
        } catch (error) {
            toast.error('Failed to fetch tickets');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchTickets();
        fetchTemples();
    }, [fetchTickets]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'open': return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-200 uppercase">Open</span>;
            case 'in_progress': return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200 uppercase">In Progress</span>;
            case 'resolved': return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-200 uppercase">Resolved</span>;
            case 'closed': return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700 border border-gray-200 uppercase">Closed</span>;
            default: return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-700 border border-gray-200 uppercase">{status}</span>;
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'text-red-600 font-bold';
            case 'high': return 'text-orange-600 font-bold';
            case 'medium': return 'text-blue-600';
            case 'low': return 'text-gray-500';
            default: return 'text-gray-500';
        }
    };

    const columns: ColumnDef<Ticket>[] = [
        {
            accessorKey: 'ticketId',
            header: 'Ticket ID',
            cell: (info) => <span className="font-mono text-xs font-bold text-orange-600">{info.getValue<string>()}</span>
        },
        {
            accessorKey: 'subject',
            header: 'Subject',
            cell: (info) => (
                <div className="flex flex-col max-w-[250px]">
                    <span className="text-sm font-bold text-gray-900 truncate">{info.getValue<string>()}</span>
                    <span className="text-[10px] text-gray-400 truncate">{info.row.original.description}</span>
                </div>
            )
        },
        {
            accessorKey: 'temple',
            header: 'Temple',
            cell: (info) => (
                <div className="flex items-center gap-2">
                    <Building size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-700">{(info.getValue<any>())?.name || 'N/A'}</span>
                </div>
            )
        },
        {
            accessorKey: 'priority',
            header: 'Priority',
            cell: (info) => (
                <span className={`text-xs capitalize ${getPriorityColor(info.getValue<string>())}`}>
                    {info.getValue<string>()}
                </span>
            )
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: (info) => getStatusBadge(info.getValue<string>())
        },
        {
            accessorKey: 'updatedAt',
            header: 'Last Activity',
            cell: (info) => (
                <div className="flex flex-col">
                    <span className="text-xs text-gray-600">{new Date(info.getValue<string>()).toLocaleDateString()}</span>
                    <span className="text-[10px] text-gray-400">{new Date(info.getValue<string>()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
            )
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (info) => (
                <button
                    onClick={() => setSelectedTicketId(info.row.original._id)}
                    className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    title="View & Reply"
                >
                    <MessageSquare size={18} />
                </button>
            )
        }
    ];

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-100 rounded-2xl">
                        <LifeBuoy className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
                        <p className="text-sm text-gray-500">Manage and respond to temple admin support requests</p>
                    </div>
                </div>
                <button
                    onClick={fetchTickets}
                    className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-all"
                >
                    <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            {/* Filters Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[300px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by ID or Subject..."
                            value={filters.search}
                            onChange={e => setFilters({ ...filters, search: e.target.value })}
                            onKeyDown={e => e.key === 'Enter' && fetchTickets()}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none transition-all"
                        />
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-all font-medium text-sm ${showFilters ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Filter size={18} /> Filters
                    </button>
                </div>

                {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t animate-in slide-in-from-top-2 duration-200">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Status</label>
                            <select
                                value={filters.status}
                                onChange={e => setFilters({ ...filters, status: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                            >
                                <option value="all">All Statuses</option>
                                <option value="open">Open</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Date Range</label>
                            <select
                                value={filters.dateRange}
                                onChange={e => setFilters({ ...filters, dateRange: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                            >
                                <option value="all">All Time</option>
                                <option value="today">Today</option>
                                <option value="week">This Week</option>
                                <option value="month">This Month</option>
                            </select>
                        </div>
                        <div className="md:col-span-1">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Temple</label>
                            <select
                                value={filters.temple}
                                onChange={e => setFilters({ ...filters, temple: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                            >
                                <option value="">All Temples</option>
                                {temples.map(t => (
                                    <option key={t._id} value={t._id}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <DataTable
                    data={tickets}
                    columns={columns}
                    loading={loading}
                    enableClientSidePagination={true}
                />
            </div>

            {/* Detail Modal */}
            {selectedTicketId && (
                <TicketDetailModalComponent
                    ticketId={selectedTicketId}
                    onClose={() => {
                        setSelectedTicketId(null);
                        fetchTickets();
                    }}
                />
            )}
        </div>
    );
};

export default TicketsPage;
